import { Children, forwardRef, memo, useCallback } from 'react';

import { loc } from '@/localization';
import { HeaderDropdown } from '@/site/components/HeaderDropdown';

import { HelpMenuItemChat, HelpMenuItemContacts, HelpMenuItemHelp } from './HelpMenuItem';
import { b, e } from './style.css';
import { UnresolvedIncidents as HelpMenuItemIncidents } from './UnresolvedIncidents';
import { useSystemStatusWatch } from './UnresolvedIncidents/useStatusWatch';

const Title = () => <span className={e.title}>{loc.Main.Help}</span>;

const HelpMenu = memo(
    forwardRef<HTMLDivElement, HelpMenu.Props>((props, ref) => {
        const { children, canWatchSystemStatus, onOpen, onClose } = props;

        const { hasUnresolvedIncidents, activate, deActivate } = useSystemStatusWatch();

        const handleOpen = useCallback(() => {
            if (onOpen) {
                onOpen();
            }

            if (canWatchSystemStatus) {
                void activate();
            }
        }, [activate, canWatchSystemStatus, onOpen]);

        const handleClose = useCallback(() => {
            if (onClose) {
                onClose();
            }

            if (canWatchSystemStatus) {
                void deActivate();
            }
        }, [canWatchSystemStatus, deActivate, onClose]);

        return (
            <div
                className={b.name}
                ref={ref}
            >
                <HeaderDropdown
                    activityStyle="underline"
                    button={<Title />}
                    onOpen={handleOpen}
                    onClose={handleClose}
                >
                    <ul className={e.items}>
                        {hasUnresolvedIncidents ? <HelpMenuItemIncidents /> : null}
                        {Children.map(children, (child) => (
                            <li className={e.item}>{child}</li>
                        ))}
                    </ul>
                </HeaderDropdown>
            </div>
        );
    }),
);
HelpMenu.displayName = 'HelpMenu';

namespace HelpMenu {
    export interface Props {
        children: React.ReactNode;
        onOpen?: () => void;
        onClose?: () => void;
        canWatchSystemStatus: boolean;
    }
}

export const DefaultHelpMenu = forwardRef<HTMLDivElement, DefaultHelpMenu.Props>((props, ref) => {
    const { helpLink } = props;
    return (
        <HelpMenu
            canWatchSystemStatus
            ref={ref}
        >
            <HelpMenuItemHelp helpLink={helpLink} />
            <HelpMenuItemChat />
            <HelpMenuItemContacts />
        </HelpMenu>
    );
});
DefaultHelpMenu.displayName = 'DefaultHelpMenu';

export namespace DefaultHelpMenu {
    export type Props = Omit<HelpMenu.Props, 'children' | 'canWatchSystemStatus'> & {
        helpLink?: string;
    };
}
